import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import emailjs from "@emailjs/browser";

// Interfaccia per i dati del form
interface FormData {
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  servizio: string;
  messaggio: string;
}

// Stati possibili del form
type FormStatus = "idle" | "loading" | "success" | "error";

// Configurazione EmailJS
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactForm: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData>({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name as keyof FormData]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome è obbligatorio";
    }

    if (!formData.cognome.trim()) {
      newErrors.cognome = "Il cognome è obbligatorio";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email è obbligatoria";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email)
    ) {
      newErrors.email = "Formato email non valido";
    }

    if (!formData.messaggio.trim()) {
      newErrors.messaggio = "Il messaggio è obbligatorio";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm() || !privacyConsent) {
      return;
    }

    setFormStatus("loading");

    try {
      const templateParams = {
        from_name: `${formData.nome} ${formData.cognome}`,
        reply_to: formData.email,
        telefono: formData.telefono,
        servizio: formData.servizio || "Non specificato",
        messaggio: formData.messaggio,
      };

      if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          templateParams,
          EMAILJS_PUBLIC_KEY
        );
      } else {
        // Simulazione invio per demo
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      setFormStatus("success");

      if (formRef.current) {
        formRef.current.reset();
      }
      setFormData({
        nome: "",
        cognome: "",
        email: "",
        telefono: "",
        servizio: "",
        messaggio: "",
      });
      setPrivacyConsent(false);

      setTimeout(() => {
        setFormStatus("idle");
      }, 5000);
    } catch (error) {
      console.error("Errore nell'invio del form:", error);
      setFormStatus("error");

      setTimeout(() => {
        setFormStatus("idle");
      }, 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="lg:w-2/3"
    >
      <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          Inviaci un Messaggio
        </h2>
        <p className="mb-8 text-gray-600">
          Compila il modulo e ti risponderemo entro 24 ore.
        </p>

        {/* Messaggi di stato */}
        {formStatus === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-800 rounded-xl flex items-center shadow-md"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-2xl text-green-600" />
            <div>
              <p className="font-semibold">Messaggio inviato con successo!</p>
              <p className="text-sm">Ti risponderemo al più presto.</p>
            </div>
          </motion.div>
        )}

        {formStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-5 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-800 rounded-xl flex items-center shadow-md"
          >
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-3 text-2xl text-red-600" />
            <div>
              <p className="font-semibold">Errore nell'invio</p>
              <p className="text-sm">Riprova più tardi o contattaci telefonicamente.</p>
            </div>
          </motion.div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="nome" className="block mb-2 text-gray-700 font-semibold">
                Nome *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className={`w-full p-3.5 border-2 ${
                  errors.nome ? "border-red-400" : "border-gray-200"
                } rounded-xl focus:outline-none focus:border-primary transition-colors bg-white text-gray-900`}
                placeholder="Il tuo nome"
                required
              />
              {errors.nome && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-xs" />
                  {errors.nome}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="cognome" className="block mb-2 text-gray-700 font-semibold">
                Cognome *
              </label>
              <input
                type="text"
                id="cognome"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                className={`w-full p-3.5 border-2 ${
                  errors.cognome ? "border-red-400" : "border-gray-200"
                } rounded-xl focus:outline-none focus:border-primary transition-colors bg-white text-gray-900`}
                placeholder="Il tuo cognome"
                required
              />
              {errors.cognome && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-xs" />
                  {errors.cognome}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block mb-2 text-gray-700 font-semibold">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full p-3.5 border-2 ${
                  errors.email ? "border-red-400" : "border-gray-200"
                } rounded-xl focus:outline-none focus:border-primary transition-colors bg-white text-gray-900`}
                placeholder="tua@email.com"
                required
              />
              {errors.email && (
                <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-xs" />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="telefono" className="block mb-2 text-gray-700 font-semibold">
                Telefono
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors bg-white text-gray-900"
                placeholder="Es. 06 12345678"
              />
            </div>
          </div>

          <div>
            <label htmlFor="servizio" className="block mb-2 text-gray-700 font-semibold">
              Servizio di interesse
            </label>
            <select
              id="servizio"
              name="servizio"
              value={formData.servizio}
              onChange={handleChange}
              className="w-full p-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors bg-white text-gray-900"
            >
              <option value="">Seleziona un servizio</option>
              <option value="luce">Offerte Luce</option>
              <option value="gas">Offerte Gas</option>
              <option value="internet">Offerte Internet</option>
              <option value="business">Soluzioni Business</option>
              <option value="altro">Altro</option>
            </select>
          </div>

          <div>
            <label htmlFor="messaggio" className="block mb-2 text-gray-700 font-semibold">
              Messaggio *
            </label>
            <textarea
              id="messaggio"
              name="messaggio"
              value={formData.messaggio}
              onChange={handleChange}
              rows={5}
              className={`w-full p-3.5 border-2 ${
                errors.messaggio ? "border-red-400" : "border-gray-200"
              } rounded-xl focus:outline-none focus:border-primary transition-colors resize-none bg-white text-gray-900`}
              placeholder="Scrivi qui il tuo messaggio..."
              required
            ></textarea>
            {errors.messaggio && (
              <p className="mt-2 text-red-500 text-sm flex items-center gap-1">
                <FontAwesomeIcon icon={faExclamationCircle} className="text-xs" />
                {errors.messaggio}
              </p>
            )}
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="privacy"
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
              className="mt-1 mr-3 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              required
            />
            <label htmlFor="privacy" className="text-gray-600 text-sm">
              Acconsento al trattamento dei miei dati personali in conformità con la{' '}
              <a
                href="/privacy-policy"
                target="_blank"
                className="text-primary hover:text-secondary font-semibold underline"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          <motion.button
            type="submit"
            disabled={formStatus === "loading"}
            className={`w-full md:w-auto px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${
              formStatus === "loading" 
                ? "bg-gray-400 cursor-not-allowed" 
                : "bg-gradient-to-r from-primary to-secondary hover:shadow-xl hover:scale-105"
            }`}
            whileHover={formStatus !== "loading" ? { scale: 1.05 } : {}}
            whileTap={formStatus !== "loading" ? { scale: 0.98 } : {}}
          >
            {formStatus === "loading" ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                Invia Messaggio
              </>
            )}
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default ContactForm;
